/* The KPI feed contract — designs/kpi-feed-seam.md KF1. A product record
   stores only a FeedRef (Trevor's decision 6: a deliverable's value is a
   pointer, never a copy); everything else here is derived at read time from
   the server seam's FeedReading. Shared because the client renders the
   reading, the products design validates the ref, and the server derives
   both — one set of names, three call sites. */

/* Which configured source, and which measure in it (KF1). */
export interface FeedRef {
  sourceId: string;
  measure: string;
}

/* What GET /api/feeds/sources lists — never a credential, never a URL. */
export interface FeedSourceInfo {
  id: string;
  label: string;
  kind: "powerbi" | "api";
}

/* Three honest states of a reading (settled decision 2, applied to a
   measurement): a value with its read time; unreachable since a time, with
   the last good read if there ever was one; or never read. */
export type FeedReading =
  | { state: "ok"; value: number; unit: string | null; readAt: string }
  | { state: "unreachable"; since: string; lastOk: { value: number; readAt: string } | null }
  | { state: "not-read-yet" };

/* The seam the routes and the certificate call (KF1/KF15). read() never
   throws for a feed's own failure — that is the unreachable state. */
export interface FeedSource {
  listSources(): FeedSourceInfo[];
  read(ref: FeedRef): Promise<FeedReading>;
}

/* Revenue and margin are refused on the measure name (KF10) — the decision
   recorded Aug 24 2026: no costs, savings, or benefit amounts in Hermes until
   financial-data permissions are settled. Matched as whole tokens after a
   light stem (trailing digits and a plural `s` stripped), so "marginal" and
   "costume" pass while "cost_per_hour", "revenues_ytd", "gross_margins" and
   "usd_2026" are refused. Kept singular — the stem supplies the plurals. */
export const FINANCIAL_MEASURE_TERMS = [
  "revenue",
  "margin",
  "profit",
  "cost",
  "saving",
  "benefit",
  "price",
  "budget",
  "usd",
] as const;

export const FINANCIAL_REFUSAL =
  "Revenue, margin and cost measures are excluded from Hermes until financial-data permissions are settled (Aug 24 2026)";

/* What a measure name may contain (KF4/KF10): what goes into a DAX
   `[measure]` reference after `]` is doubled, plus `:` so the seed source's
   `:down` outage marker (KF7) is a legal measure. */
export const MEASURE_PATTERN = /^[\w %./:-]+$/;
export const MEASURE_MAX_LENGTH = 200;

const financialTokens = new Set<string>(FINANCIAL_MEASURE_TERMS);

/* The one validator for a FeedRef (KF10). `sources` is the server's list when
   the caller has it (the routes); the products design's evidence validator
   has no list and judges the shape alone. null = acceptable. */
export function feedRefReason(ref: unknown, sources?: readonly FeedSourceInfo[]): string | null {
  const r = (ref ?? {}) as Partial<Record<keyof FeedRef, unknown>>;
  if (typeof ref !== "object" || typeof r.sourceId !== "string" || r.sourceId.trim() === "")
    return "Pick a feed source";
  if (sources && !sources.some((s) => s.id === r.sourceId))
    return `"${r.sourceId}" is not a configured feed source`;
  if (typeof r.measure !== "string" || r.measure.trim() === "") return "Enter the measure to read";
  if (r.measure.length > MEASURE_MAX_LENGTH)
    return `The measure name must be ${MEASURE_MAX_LENGTH} characters or fewer`;
  if (!MEASURE_PATTERN.test(r.measure))
    return "The measure name may use letters, digits, space, _ % . / : -";
  if (namesFinancialTerm(r.measure)) return FINANCIAL_REFUSAL;
  return null;
}

/* Does a name use one of the financial terms (KF10)? Also how a team
   baseline's text cells are checked (product-baselines.md BL8). */
export function namesFinancialTerm(name: string): boolean {
  /* split on every non-word character AND the underscore, which \w keeps —
     "cost_per_hour" must yield the token "cost"; then stem: "revenues2026"
     → "revenue", "prices" → "price" */
  return name
    .toLowerCase()
    .split(/[\W_]+/)
    .filter(Boolean)
    .map((t) => t.replace(/\d+$/, "").replace(/s$/, ""))
    .some((t) => financialTokens.has(t));
}

/* The catalog's machine-readable target (pdp-catalog.md, `PdpCriterion.target`),
   named structurally here: the seam compares against it and never against a
   product-side number (KF13). */
export interface FeedTarget {
  op: ">=" | "<=" | ">" | "<";
  value: number;
  unit: string;
}

export type MetState = "met" | "not-met";

/* Met / not met (KF13): only an ok reading against a non-null target can be
   judged; anything else is null — the cell shows the value with a neutral
   chip, never a guessed verdict. */
export function metState(reading: FeedReading, target: FeedTarget | null): MetState | null {
  if (reading.state !== "ok" || target === null) return null;
  const v = reading.value;
  const holds =
    target.op === ">="
      ? v >= target.value
      : target.op === "<="
        ? v <= target.value
        : target.op === ">"
          ? v > target.value
          : v < target.value;
  return holds ? "met" : "not-met";
}

/* One logged read (KF9) — the flat shape both stores hold. An ok row carries
   itself as lastOk; an unreachable row carries `since` and the last good read
   copied forward from the row before it. */
export interface FeedReadRow extends FeedRef {
  readAt: string;
  state: "ok" | "unreachable";
  value: number | null;
  unit: string | null;
  since: string | null;
  lastOkValue: number | null;
  lastOkAt: string | null;
}

/* The latest row alone answers the reading (KF9) — which is what lets the
   log be pruned to a short trail without losing what the UI shows. */
export function readingFrom(row: FeedReadRow | null): FeedReading {
  if (row === null) return { state: "not-read-yet" };
  if (row.state === "ok")
    return { state: "ok", value: row.value as number, unit: row.unit, readAt: row.readAt };
  return {
    state: "unreachable",
    since: row.since as string,
    lastOk:
      row.lastOkValue !== null && row.lastOkAt !== null
        ? { value: row.lastOkValue, readAt: row.lastOkAt }
        : null,
  };
}
